import React, { useCallback, useMemo, useState } from 'react';
import { ScrollView, StyleSheet } from 'react-native';
import {
  useResponsive,
  useSafeNavigation,
  useSafeRouter,
  useToggleState,
} from '@sg/garnish';

import { useFeatureFlag } from '@order/LaunchDarkly';
import { useIsExpoRouterEnabled } from '@order/shared/hooks';
import { useTelemetry, useTrackEventOnFocus } from '@order/Telemetry';

import { AppFooter, LoadingAnimation } from '../../components';
import { useIsLoggedIn } from '../../context/AuthMachine';
import {
  GiftCardsErrorView,
  GiftCardsSignedOutModal,
  GiftCardsWrapper,
} from './components';
import { useGiftCards } from './hooks';
import {
  GiftCardsScreenCategoriesGrid,
  GiftCardsScreenCategoriesNav,
  GiftCardsScreenHeader,
} from './sections';

// ─────────────────────────────────────────────────────────────────────────────

export const GiftCardsScreen = () => {
  const { data, error, fetching } = useGiftCards();
  const { heading, subheading, subheadingLinkText, giftCardCategories } = data;

  const isExpoRouterEnabled = useIsExpoRouterEnabled();
  const router = useSafeRouter();

  const { track } = useTelemetry();
  const isSignedIn = useIsLoggedIn();
  const navigation = useSafeNavigation();
  const { currentBreakpoint } = useResponsive();
  const isInStoreOrdersEnabled = useFeatureFlag('CELS-4174-in-store-loyalty-orders-enabled'); // prettier-ignore

  // ─── State ───────────────────────────────────────────────────────────

  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(
    null,
  );

  const {
    value: isRedeemGiftCardSignedOutModalShown,
    toggle: toggleRedeemGiftCardSignedOutModal,
  } = useToggleState(false);
  const {
    value: isGiftCardSignedOutModalShown,
    toggle: toggleGiftCardSignedOutModal,
  } = useToggleState(false);

  // ─── Derived Data ────────────────────────────────────────────────────

  const filteredCategories = useMemo(
    () =>
      giftCardCategories.filter((category) => {
        const { id: categoryId } = category.fields;

        return !selectedCategoryId || categoryId === selectedCategoryId;
      }),
    [giftCardCategories, selectedCategoryId],
  );

  const hasCategories = filteredCategories.length > 0;
  const shouldRenderGiftCardsList = !fetching;

  // ─── Helpers ─────────────────────────────────────────────────────────

  const navigateToAddGiftCardScreen = useCallback(() => {
    track('gift.redeem_cta_tapped');

    if (!isSignedIn) {
      toggleRedeemGiftCardSignedOutModal();

      return;
    }

    if (currentBreakpoint.isXS) {
      if (isExpoRouterEnabled) {
        router?.navigate('/add-gift-card');
        return;
      }

      navigation?.navigate('Modal', { screen: 'AddGiftCard' });
      return;
    }

    if (isInStoreOrdersEnabled) {
      if (isExpoRouterEnabled) {
        router?.navigate('/account/gift-cards');
        return;
      }

      navigation?.navigate('MainTabs', {
        screen: 'AccountTab',
        params: { screen: 'GiftCards', initial: false },
      });
      return;
    }

    if (isExpoRouterEnabled) {
      router?.navigate('/account/payment-and-gift-cards');
      return;
    }

    navigation?.navigate('MainTabs', {
      screen: 'AccountTab',
      params: { screen: 'PaymentAndGiftCards', initial: false },
    });
  }, [
    track,
    isSignedIn,
    currentBreakpoint.isXS,
    isInStoreOrdersEnabled,
    isExpoRouterEnabled,
    router,
    navigation,
    toggleRedeemGiftCardSignedOutModal,
  ]);

  // ─── Effects ─────────────────────────────────────────────────────────

  useTrackEventOnFocus(
    { name: 'gift.view.success' },
    { skip: !shouldRenderGiftCardsList },
  );

  // ─────────────────────────────────────────────────────────────────────

  if (fetching) {
    return <LoadingAnimation size="large" />;
  }

  return (
    <GiftCardsWrapper>
      <ScrollView
        stickyHeaderIndices={[1]}
        contentContainerStyle={styles.contentContainer}
      >
        <GiftCardsScreenHeader
          heading={heading}
          subheading={subheading}
          subheadingLinkText={subheadingLinkText}
          opensDialog={!isSignedIn}
          onSubheadingLinkPress={navigateToAddGiftCardScreen}
        />

        {hasCategories ? (
          <GiftCardsScreenCategoriesNav
            categories={giftCardCategories}
            selectedCategoryId={selectedCategoryId}
            setSelectedCategoryId={setSelectedCategoryId}
          />
        ) : null}

        {error ? <GiftCardsErrorView /> : null}

        {hasCategories ? (
          <GiftCardsScreenCategoriesGrid
            categories={filteredCategories}
            isSignedIn={isSignedIn}
            onSignedOutCardPress={toggleGiftCardSignedOutModal}
          />
        ) : null}

        {currentBreakpoint.isXS ? null : <AppFooter />}
      </ScrollView>

      <GiftCardsSignedOutModal
        isVisible={isRedeemGiftCardSignedOutModalShown}
        toggleModal={toggleRedeemGiftCardSignedOutModal}
        redirectTo="payment-and-gift-cards"
      />

      <GiftCardsSignedOutModal
        isVisible={isGiftCardSignedOutModalShown}
        toggleModal={toggleGiftCardSignedOutModal}
        redirectTo="gift-cards"
      />
    </GiftCardsWrapper>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  contentContainer: {
    minHeight: '100%',
  },
});
